/**
 * INTERACTIVE MUTUAL FUND RESEARCH — a session with the MF research analyst.
 *
 *   useStartMfResearchSession  opens a session on one fund plan (window,
 *                              seed), or a new one generated from an earlier
 *                              session.
 *   useMfAnalystSessions       the reader's sessions on a fund, to resume.
 *   useMfAnalystSession        one session — the shared machinery
 *                              (analystSession.js: chat, approvals, PDF,
 *                              closing) plus what only this analyst has:
 *                              editing one part of the note, applying
 *                              assumptions (with its conflict round-trip),
 *                              refreshing the data, and reverting to an
 *                              earlier revision.
 */
import { useCallback, useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  MF_ANALYST_NOTE_CHECK, MF_ANALYST_NOTE_CONTRACT, MF_ANALYST_PENDING_ACTIONS, MF_ANALYST_REVISION, MF_ANALYST_REVISIONS,
  MF_ANALYST_SESSION, MF_ANALYST_SESSIONS,
} from '../queries/mfAnalyst';
import {
  APPLY_MF_ANALYST_ASSUMPTIONS, CLOSE_MF_ANALYST_SESSION, CONFIRM_MF_ANALYST_ACTION, DOWNLOAD_MF_ANALYST_REPORT,
  ISSUE_MF_ANALYST_TOKEN, REFRESH_MF_ANALYST_DATA, REJECT_MF_ANALYST_ACTION, REVERT_MF_ANALYST_REVISION, REVISE_MF_ANALYST_NOTE,
  START_MF_ANALYST_SESSION, START_MF_ANALYST_UPDATE_SESSION, WRITE_MF_ANALYST_NOTE,
} from '../mutations/mfAnalyst';
import { MF_ANALYST_CHAT } from '../subscriptions/mfAnalyst';
import { EMPTY } from './_shared';
import { parseJson, useAnalystSession } from './analystSession';

/** The research windows a session can be opened on, and what it starts from. */
export const MF_RESEARCH_WINDOWS = [
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'yearly', label: 'Yearly' },
  { id: 'monthly', label: 'Monthly' },
];
export const MF_RESEARCH_SEEDS = [
  { id: 'latest_report', label: 'From the last published report' },
  { id: 'empty', label: 'An empty note' },
];

/** The skeleton of a whole note, in the contract's output shape — the starting point for writing one. */
export const MF_NOTE_TEMPLATE = {
  headline: '',
  brief: '',
  pillar_summaries: {},
  research_verdict: { call: '' },
  findings: [],
};

/** `{ clean, errors }` from the note check, whatever shape the scalar arrives in. */
export function readNoteCheck(raw) {
  const v = parseJson(raw);
  if (!v || typeof v !== 'object') return null;
  const errors = Array.isArray(v.errors) ? v.errors : v.errors ? [v.errors] : [];
  return { clean: Boolean(v.clean) && !errors.length, errors, raw: v };
}
/** The parts of the note `reviseMfResearchAnalystNote` edits directly. */
export const MF_NOTE_FIELDS = [
  { id: 'headline', label: 'Headline' },
  { id: 'brief', label: 'Brief' },
  { id: 'objective_and_style', label: 'Objective and style' },
  { id: 'market_context', label: 'Market context' },
  { id: 'whats_working', label: 'What is working' },
  { id: 'whats_not_working', label: 'What is not working' },
  { id: 'what_would_change_it', label: 'What would change it' },
  { id: 'talking_points', label: 'Talking points' },
  { id: 'performance_risks', label: 'Performance risks' },
];
/** What applying assumptions answers when it wrote nothing and wants a decision. */
export const isConflictStatus = (status) => /CONFLICT/i.test(String(status || ''));

const MF_API = {
  key: 'mfAnalyst',
  docs: {
    session: MF_ANALYST_SESSION,
    revisions: MF_ANALYST_REVISIONS,
    revision: MF_ANALYST_REVISION,
    pending: MF_ANALYST_PENDING_ACTIONS,
    chat: MF_ANALYST_CHAT,
    confirm: CONFIRM_MF_ANALYST_ACTION,
    reject: REJECT_MF_ANALYST_ACTION,
    token: ISSUE_MF_ANALYST_TOKEN,
    close: CLOSE_MF_ANALYST_SESSION,
    download: DOWNLOAD_MF_ANALYST_REPORT,
  },
  field: {
    session: 'mfResearchAnalystSession',
    revisions: 'mfResearchAnalystRevisions',
    revision: 'mfResearchAnalystRevision',
    pending: 'mfResearchAnalystPendingActions',
    chat: 'mfResearchAnalystChat',
    confirm: 'confirmMfResearchAnalystAction',
    reject: 'rejectMfResearchAnalystAction',
    token: 'issueMfResearchAnalystConfirmationToken',
    close: 'closeMfResearchAnalystSession',
    download: 'downloadMfResearchAnalystReport',
  },
  sessionsQuery: 'MfResearchAnalystSessions',
};

/**
 * `start(symbol, { window, seed })` opens a session; `startFrom(previousId)`
 * generates a new one from an earlier session. Both answer `{ session, … }`.
 */
export function useStartMfResearchSession() {
  const opts = { fetchPolicy: 'no-cache', refetchQueries: ['MfResearchAnalystSessions'] };
  const [run, runState] = useMutation(START_MF_ANALYST_SESSION, opts);
  const [runUpdate, updateState] = useMutation(START_MF_ANALYST_UPDATE_SESSION, opts);
  const start = useCallback(async (securitySymbol, { window = 'quarterly', seed = 'latest_report' } = {}) => {
    const { data } = await run({ variables: { securitySymbol, window, seed } });
    return data?.startMfResearchAnalystSession ?? null;
  }, [run]);
  const startFrom = useCallback(async (previousSessionId, { changes = null, proceedWithConflicts = false } = {}) => {
    const { data } = await runUpdate({ variables: { previousSessionId: Number(previousSessionId), changes, proceedWithConflicts } });
    return data?.startMfResearchAnalystUpdateSession ?? null;
  }, [runUpdate]);
  return {
    start, startFrom, starting: runState.loading || updateState.loading, error: runState.error ?? updateState.error ?? null,
  };
}

/** The reader's sessions on one fund, newest first. */
export function useMfAnalystSessions(securitySymbol, { limit = 10, skip = false } = {}) {
  const q = useQuery(MF_ANALYST_SESSIONS, {
    variables: { securitySymbol, limit },
    skip: skip || !securitySymbol,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return {
    sessions: q.data?.mfResearchAnalystSessions ?? EMPTY,
    loading: q.loading,
    error: q.error ?? null,
    refetch: q.refetch,
  };
}

/** One MF research session. Mount it keyed by the session id. */
export function useMfAnalystSession(sessionId) {
  const base = useAnalystSession(sessionId, MF_API);
  const { id, reload, pickRevision } = base;
  const opts = { fetchPolicy: 'no-cache' };
  const [reviseNote, reviseState] = useMutation(REVISE_MF_ANALYST_NOTE, opts);
  const [applyAssumptions, applyState] = useMutation(APPLY_MF_ANALYST_ASSUMPTIONS, opts);
  const [refreshData, refreshState] = useMutation(REFRESH_MF_ANALYST_DATA, opts);
  const [revertRevision, revertState] = useMutation(REVERT_MF_ANALYST_REVISION, opts);
  /* The last assumptions answer that wrote nothing: its conflicts and options, kept until decided. */
  const [conflict, setConflict] = useState(null);

  /* Every edit writes a new revision: read it, and show it. */
  const landed = useCallback(async (rev) => {
    await reload();
    pickRevision(null);
    return rev;
  }, [reload, pickRevision]);

  const revise = useCallback(async ({ field, text, mode = 'replace' }) => {
    const { data } = await reviseNote({ variables: { sessionId: id, field, text, mode } });
    return landed(data?.reviseMfResearchAnalystNote ?? null);
  }, [id, reviseNote, landed]);

  /** Answers the whole result; on CONFLICTS_NEED_DECISION nothing was written and `conflict` holds it. */
  const assume = useCallback(async (changes, { proceedWithConflicts = false } = {}) => {
    const { data } = await applyAssumptions({ variables: { sessionId: id, changes, proceedWithConflicts } });
    const out = data?.applyMfResearchAnalystAssumptions ?? null;
    if (isConflictStatus(out?.status)) {
      setConflict({ changes, ...out });
    } else {
      setConflict(null);
      await landed(out?.revision ?? null);
    }
    return out;
  }, [id, applyAssumptions, landed]);

  const refresh = useCallback(async () => {
    const { data } = await refreshData({ variables: { sessionId: id } });
    return landed(data?.refreshMfResearchAnalystData ?? null);
  }, [id, refreshData, landed]);

  /* ── the note contract, writing the whole note, and the publish checks ── */
  const client = useApolloClient();
  const [contract, setContract] = useState({ loading: false, value: null, error: null });
  const loadContract = useCallback(async () => {
    setContract((c) => ({ ...c, loading: true, error: null }));
    try {
      const { data } = await client.query({ query: MF_ANALYST_NOTE_CONTRACT, variables: { sessionId: id }, fetchPolicy: 'network-only' });
      setContract({ loading: false, value: parseJson(data?.mfResearchAnalystNoteContract), error: null });
    } catch (e) {
      setContract({ loading: false, value: null, error: e });
    }
  }, [client, id]);

  /* The check describes the note as it stands, so it is dropped whenever a new revision lands. */
  const [check, setCheck] = useState({ loading: false, value: null, error: null, forRevision: null });
  const runCheck = useCallback(async () => {
    setCheck((c) => ({ ...c, loading: true, error: null }));
    try {
      const { data } = await client.query({ query: MF_ANALYST_NOTE_CHECK, variables: { sessionId: id }, fetchPolicy: 'network-only' });
      const value = readNoteCheck(data?.mfResearchAnalystNoteCheck);
      setCheck({ loading: false, value, error: null, forRevision: base.latestId });
      return value;
    } catch (e) {
      setCheck({ loading: false, value: null, error: e, forRevision: null });
      throw e;
    }
  }, [client, id, base.latestId]);
  const checkNow = useMemo(
    () => (check.forRevision != null && Number(check.forRevision) === Number(base.latestId) ? check : { ...check, value: null }),
    [check, base.latestId],
  );

  const [writeNoteMut, writeState] = useMutation(WRITE_MF_ANALYST_NOTE, opts);
  /** The whole note becomes the base note; the checks are run on it straight after. */
  const writeNote = useCallback(async (note) => {
    const { data } = await writeNoteMut({ variables: { sessionId: id, note } });
    const rev = await landed(data?.writeMfResearchAnalystNote ?? null);
    runCheck().catch(() => {});
    return rev;
  }, [id, writeNoteMut, landed, runCheck]);

  const revert = useCallback(async (revisionNumber) => {
    const { data } = await revertRevision({ variables: { sessionId: id, revisionNumber: Number(revisionNumber) } });
    return landed(data?.revertMfResearchAnalystRevision ?? null);
  }, [id, revertRevision, landed]);

  return useMemo(() => ({
    ...base,
    revise,
    revising: reviseState.loading,
    assume,
    assuming: applyState.loading,
    conflict,
    dismissConflict: () => setConflict(null),
    refresh,
    refreshing: refreshState.loading,
    revert,
    reverting: revertState.loading,
    contract,
    loadContract,
    check: checkNow,
    runCheck,
    writeNote,
    writingNote: writeState.loading,
  }), [
    base, revise, reviseState.loading, assume, applyState.loading, conflict, refresh, refreshState.loading, revert, revertState.loading,
    contract, loadContract, checkNow, runCheck, writeNote, writeState.loading,
  ]);
}
